import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/lib/Modal';
import classnames from 'classnames';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import Button from '@/shared/components/Button';
import intlShape from '@/shared/utils/intlShape';
import TranslatedFilePond from '@/shared/components/TranslatedFilePond';
import MimeIcon from '@/shared/components/MimeIcon';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import messages from './documentTemplateMessages';

class AddDocumentTemplateModal extends Component {
  constructor(props) {
    super(props);
    const { templateType, language, companyId } = props;
    this.state = {
      template: {
        id: null,
        name: '',
        templateType: templateType || null,
        language: language || null,
        companyId: companyId || null,
      },
      showValidation: false,
      saving: false,
    };
  }

  updateTemplate = changes => {
    this.setState(({ template }) => ({ template: { ...template, ...changes } }));
  };

  save = () => {
    const { saving, template, file } = this.state;
    const { onClose } = this.props;
    if (!this.isValid() || saving) return '';
    this.setState({ saving: true });
    const formData = new FormData();
    formData.append('name', template.name);
    formData.append('templateType', template.templateType);
    formData.append('languageCode', template.language);
    formData.append('relationType', template.relationType);
    formData.append('companyId', template.companyId);
    formData.append('data', file);
    return api.base
      .post(`${config.appUrls.api}/api/documentTemplate/Template`, formData, {
        headers: {
          'Content-Type': undefined,
        },
      })
      .then(
        ({ data }) => {
          if (!data) return '';

          onClose(data);
          return data;
        },
        error => {
          const { intl } = this.props;
          handleHttpError(intl)(error);
          return error;
        },
      )
      .finally(() => {
        if (typeof this !== 'undefined') this.setState({ saving: false });
      });
  };
  showValidation = () => {
    const { showValidation } = this.state;
    if (!showValidation) this.setState({ showValidation: true });
  };

  setFile = file => {
    this.setState({ file: file.file });
    this.pond.removeFile();
  };

  isValid = () => {
    const { file, template } = this.state;
    return (
      !!file && template.templateType !== null && template.language !== null && !!template.name
    );
  };

  render() {
    const { onClose, templateTypes, languages, relationTypes, companies, intl } = this.props;
    const { template, showValidation, saving, file } = this.state;

    const isValid = this.isValid();
    const multipleCompanies = companies.length > 1;
    return (
      <Modal backdrop="static" show onHide={() => onClose(null)} keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FormattedMessage {...messages.AddDocumentTemplate} />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-horizontal">
            <div
              className={classnames('form-group', {
                'has-error': showValidation && template.templateType === null,
              })}
            >
              <label className="control-label col-sm-4">
                <FormattedMessage {...messages.TemplateType} />
              </label>
              <div className="col-sm-8">
                <select
                  className="form-control"
                  value={template.templateType || ''}
                  onChange={e => this.updateTemplate({ templateType: e.target.value })}
                >
                  {template.templateType === null && <option value="" />}
                  {templateTypes.map(x => (
                    <option key={x.id} value={x.id}>
                      {x.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div
              className={classnames('form-group', {
                'has-error': showValidation && !template.name,
              })}
            >
              <label className="control-label col-sm-4">
                <FormattedMessage {...messages.Name} />
              </label>
              <div className="col-sm-8">
                <input
                  type="text"
                  className="form-control"
                  value={template.name}
                  placeholder={intl.formatMessage(messages.Name)}
                  required
                  onChange={e => this.updateTemplate({ name: e.target.value })}
                />
              </div>
            </div>
            <div
              className={classnames('form-group', {
                'has-error': showValidation && template.language === null,
              })}
            >
              <label className="control-label col-sm-4">
                <FormattedMessage {...messages.Language} />
              </label>
              <div className="col-sm-8">
                <select
                  className="form-control"
                  value={template.language || ''}
                  onChange={e => this.updateTemplate({ language: e.target.value })}
                >
                  {template.language === null && <option value="" />}
                  {languages.map(x => (
                    <option key={x.id} value={x.id}>
                      {x.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-4">
                <FormattedMessage {...messages.RelationType} />
              </label>
              <div className="col-sm-8">
                <select
                  className="form-control"
                  value={template.relationType || ''}
                  onChange={e =>
                    this.updateTemplate({
                      relationType: e.target.value === '' ? null : e.target.value,
                    })
                  }
                >
                  <option value="">{intl.formatMessage(messages.All)}</option>
                  {relationTypes.map(x => (
                    <option key={x.id} value={x.id}>
                      {x.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {multipleCompanies && (
              <div className="form-group">
                <label className="control-label col-sm-4">
                  <FormattedMessage {...messages.OwnCompany} />
                </label>
                <div className="col-sm-8">
                  <select
                    className="form-control"
                    value={template.companyId || ''}
                    onChange={e =>
                      this.updateTemplate({
                        companyId: e.target.value === '' ? null : e.target.value,
                      })
                    }
                  >
                    <option value="">{intl.formatMessage(messages.All)}</option>
                    {companies.map(x => (
                      <option key={x.id} value={x.id}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            {file && (
              <div className="form-group">
                <label className="control-label col-sm-4">
                  <FormattedMessage {...messages.File} />
                </label>
                <div className="col-sm-8">
                  <p className="form-control-static">
                    <MimeIcon mime={file.type} />
                    <span>{file.name}</span>
                  </p>
                </div>
              </div>
            )}
            <div
              className={classnames('form-group', {
                'has-error': showValidation && !file,
              })}
            >
              <div className="col-sm-12">
                <div className="file-upload-wrapper">
                  <TranslatedFilePond
                    onaddfile={(error, newFile) => this.setFile(newFile)}
                    pondRef={ref => {
                      this.pond = ref;
                    }}
                    acceptedFileTypes={[
                      'application/msword',
                      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                      'application/vnd.ms-excel',
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={saving} onClick={() => onClose(null)}>
            <FormattedMessage {...messages.Cancel} />
          </Button>
          <Button
            onClick={() => this.save()}
            disabled={showValidation && !isValid}
            onFocus={this.showValidation}
            onMouseOver={this.showValidation}
            btnClassName="btn-inverse"
            showProgress
          >
            <FormattedMessage {...messages.Add} />
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

AddDocumentTemplateModal.propTypes = {
  intl: intlShape.isRequired,
  onClose: PropTypes.func.isRequired,
  templateTypes: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, label: PropTypes.string }),
  ).isRequired,
  relationTypes: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, label: PropTypes.string }),
  ).isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string }))
    .isRequired,
  companies: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string }))
    .isRequired,

  templateType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  language: PropTypes.string,
  companyId: PropTypes.string,
};

AddDocumentTemplateModal.defaultProps = {
  templateType: null,
  language: null,
  companyId: null,
};

export default injectIntl(AddDocumentTemplateModal);
